import { useCallback, useRef, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { isAfter, parseISO } from "date-fns";
import {
    ActionButton,
    Alert,
    Button,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";

import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { AppointmentLocation } from "../../../../utils/types/session";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import { TERMINATION_OF_TREATMENT_NOTE_LABELS } from "../../../../utils/constants";
import {
    IEditTerminationOfTreatmentPayload,
    IGetClientNotes,
    ISessionNote,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";

import DeleteIcon from "../../../../icons/Delete";
import { IViewFile } from "../../../../utils/types";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditTerminationOfTreatmentProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function EditTerminationOfTreatment({
    noteDetails,
}: EditTerminationOfTreatmentProps) {
    // Local component state
    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    const [localFirstSessionDate, setLocalFirstSessionDate] =
        useState<Date | null>(
            noteDetails.note_content?.first_session_date
                ? parseISO(noteDetails.note_content.first_session_date)
                : null
        );
    const [localLastSessionDate, setLocalLastSessionDate] =
        useState<Date | null>(
            noteDetails.note_content?.last_session_date
                ? parseISO(noteDetails.note_content.last_session_date)
                : null
        );

    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);
    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const isSigning = useRef(false);

    const {
        register,
        handleSubmit,
        getValues,
        watch,
        setValue,
        control,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditTerminationOfTreatmentPayload>({
        resolver: yupResolver(
            yup.object({
                duration: yup.string(),
                appointment_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Session location is required"),
                    otherwise: yup.string(),
                }),
                total_number_of_sessions: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Total number of sessions is required")
                        .test(
                            "is-number",
                            "Field should only contain numbers",
                            (value) => {
                                if (!value || value.trim() === "") {
                                    return true;
                                }

                                const regex = /^\d+$/;
                                return regex.test(value);
                            }
                        ),
                    otherwise: yup
                        .string()
                        .test(
                            "is-number",
                            "Field should only contain numbers",
                            (value) => {
                                if (!value || value.trim() === "") {
                                    return true;
                                }

                                const regex = /^\d+$/;
                                return regex.test(value);
                            }
                        ),
                }),
                first_session_date: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup.date().required("First session date is required"),
                    otherwise: yup.date().nullable(),
                }),
                last_session_date: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .date()
                        .required("Last session date is required")
                        .test(
                            "is-after-or-equal",
                            `Last session date cannot be earlier than first session date`,
                            (
                                value,
                                context: {
                                    parent: {
                                        first_session_date: Date;
                                    };
                                }
                            ) => {
                                const { first_session_date: firstSessionDate } =
                                    context.parent;

                                if (!firstSessionDate || !value) {
                                    // If either date is not provided, don't perform the validation
                                    return true;
                                }

                                const firstSessionDateCopy = firstSessionDate;

                                firstSessionDateCopy.setHours(0, 0, 0, 0);

                                return !isAfter(firstSessionDateCopy, value);
                            }
                        ),
                    otherwise: yup
                        .date()
                        .test(
                            "is-after-or-equal",
                            `Last session date cannot be earlier than first session date`,
                            (
                                value,
                                context: {
                                    parent: {
                                        first_session_date: Date;
                                    };
                                }
                            ) => {
                                const { first_session_date: firstSessionDate } =
                                    context.parent;

                                if (!firstSessionDate || !value) {
                                    // If either date is not provided, don't perform the validation
                                    return true;
                                }

                                const firstSessionDateCopy = firstSessionDate;

                                firstSessionDateCopy.setHours(0, 0, 0, 0);

                                return !isAfter(firstSessionDateCopy, value);
                            }
                        )
                        .nullable(),
                }),
                delivery_option: yup
                    .string()
                    .nullable()
                    .when(
                        "appointment_location",
                        (appointment_location, field) =>
                            appointment_location === "teletherapy" &&
                            isSigning.current
                                ? field.required(
                                      "Client teletherapy location is required"
                                  )
                                : field
                    ),
                provider_location: yup
                    .string()
                    .nullable()
                    .when(
                        "appointment_location",
                        (appointment_location, field) =>
                            appointment_location === "teletherapy" &&
                            isSigning.current
                                ? field.required(
                                      "Provider teletherapy location is required"
                                  )
                                : field
                    ),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_teletherapy_session_was_conducted_via_video: yup.bool(),
                other_delivery_option: yup
                    .string()
                    .when("delivery_option", (delivery_option, field) =>
                        delivery_option === "Other" && isSigning.current
                            ? field.required(
                                  "Other client teletherapy location is required"
                              )
                            : field
                    ),
                other_provider_location: yup
                    .string()
                    .when("provider_location", (provider_location, field) =>
                        provider_location === "Other" && isSigning.current
                            ? field.required(
                                  "Other provider teletherapy location is required"
                              )
                            : field
                    ),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                }),
                secondary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                tertiary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_one: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_two: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                presenting_problems: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Presenting problems is required"),
                    otherwise: yup.string(),
                }),
                planned_treatment_and_goals: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string(),
                }),
                course_of_treatment: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Course of treatment is required"),
                    otherwise: yup.string(),
                }),
                patient_final_condition: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Patient final condition is required"),
                    otherwise: yup.string(),
                }),
                prognosis: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Prognosis is required"),
                    otherwise: yup.string(),
                }),
                reason_for_termination: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Reason for termination is required"),
                    otherwise: yup.string(),
                }),
                discharge_plan_and_follow_up: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Field is required"),
                    otherwise: yup.string(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Signature is required"),
                    otherwise: yup.string(),
                }),
                treatment_modality_intervention: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup.array().min(1).required("This field is required"),
                    otherwise: yup.array().nullable(),
                }),
                other_treatment_modality_intervention: yup.string(),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: [
                      noteDetails?.note_content?.secondary_diagnosis,
                  ],
                  tertiary_diagnosis: [
                      noteDetails?.note_content?.tertiary_diagnosis,
                  ],
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    // Get appointment location options
    const AppointmentLocationsForSelect = Object.values(
        AppointmentLocation
    ).map((location) => {
        return {
            label: removeEnumUnderscore(makeStringFirstLetterCapital(location)),
            value: location,
        };
    });

    // Function to handle item click and set the selected note content
    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "appointment_location",
            note.note_content.appointment_location
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue("course_of_treatment", note.note_content.course_of_treatment);
        setValue("delivery_option", note.note_content.delivery_option);
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue(
            "discharge_plan_and_follow_up",
            note.note_content.discharge_plan_and_follow_up
        );
        setValue("duration", note.note_content.duration);
        setValue("first_session_date", note.note_content.first_session_date);
        setValue("last_session_date", note.note_content.last_session_date);
        setValue(
            "other_delivery_option",
            note.note_content.other_delivery_option
        );
        setValue(
            "other_provider_location",
            note.note_content.other_provider_location
        );
        setValue(
            "patient_final_condition",
            note.note_content.patient_final_condition
        );
        setValue(
            "planned_treatment_and_goals",
            note.note_content.planned_treatment_and_goals
        );
        setValue("presenting_problems", note.note_content.presenting_problems);
        setValue("prognosis", note.note_content.prognosis);
        setValue("provider_location", note.note_content.provider_location);
        setValue(
            "reason_for_termination",
            note.note_content.reason_for_termination
        );
        setValue(
            "the_teletherapy_session_was_conducted_via_video",
            note.note_content.the_teletherapy_session_was_conducted_via_video
        );
        setValue(
            "total_number_of_sessions",
            note.note_content.total_number_of_sessions
        );
        setValue(
            "treatment_modality_intervention",
            note.note_content.treatment_modality_intervention
        );
        setValue(
            "other_treatment_modality_intervention",
            note.note_content.other_treatment_modality_intervention
        );
        setShowPrompt(false);
    };

    const isCheckboxChecked = (checkboxValue: string, key: string) => {
        return Boolean(
            noteDetails.note_content?.[key]?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <div>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}
            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="termination form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>

                <div className="fg">
                    <Controller
                        name="appointment_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    TERMINATION_OF_TREATMENT_NOTE_LABELS.appointment_location
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    AppointmentLocationsForSelect.find(
                                        (location) =>
                                            location.value === field.value
                                    ) || null
                                }
                                options={AppointmentLocationsForSelect}
                                hasError={!!errors.appointment_location}
                                errorText={errors.appointment_location?.message}
                            />
                        )}
                    />
                </div>
                {watch(`appointment_location`) ===
                    AppointmentLocation.TELETHERAPY && (
                    <>
                        <p className="note-label">
                            {
                                TERMINATION_OF_TREATMENT_NOTE_LABELS.delivery_option
                            }
                        </p>
                        <div className="fg fg-space-between three flex">
                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Home"
                                value="Home"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Home"
                                }
                            />
                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Office"
                                value="Office"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Office"
                                }
                            />
                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Other"
                                value="Other"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Other"
                                }
                            />
                        </div>
                        {errors.delivery_option ? (
                            <div className="note-error">
                                <p>Delivery option is required</p>
                            </div>
                        ) : null}
                        {watch(`delivery_option`) === "Other" ||
                        noteDetails.note_content?.delivery_option ===
                            "Other" ? (
                            <div className="fg">
                                <Input
                                    {...register(`other_delivery_option`)}
                                    label={
                                        TERMINATION_OF_TREATMENT_NOTE_LABELS.other_delivery_option
                                    }
                                    hasError={!!errors?.other_delivery_option}
                                    errorText={
                                        errors?.other_delivery_option?.message
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_delivery_option
                                    }
                                />
                            </div>
                        ) : null}
                    </>
                )}
                {watch(`appointment_location`) ===
                    AppointmentLocation.TELETHERAPY && (
                    <>
                        <p className="note-label">
                            {
                                TERMINATION_OF_TREATMENT_NOTE_LABELS.provider_location
                            }
                        </p>
                        <div className="fg fg-space-between three flex">
                            <RadioInput
                                {...register(`provider_location`)}
                                label="Home"
                                value="Home"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Home"
                                }
                            />
                            <RadioInput
                                {...register(`provider_location`)}
                                label="Office"
                                value="Office"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Office"
                                }
                            />
                            <RadioInput
                                {...register(`provider_location`)}
                                label="Other"
                                value="Other"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Other"
                                }
                            />
                        </div>
                        {errors.provider_location ? (
                            <div className="note-error">
                                <p>Delivery option is required</p>
                            </div>
                        ) : null}
                        {watch(`provider_location`) === "Other" ||
                        noteDetails.note_content?.provider_location ===
                            "Other" ? (
                            <div className="fg">
                                <Input
                                    {...register(`other_provider_location`)}
                                    label={
                                        TERMINATION_OF_TREATMENT_NOTE_LABELS.other_provider_location
                                    }
                                    hasError={!!errors?.other_provider_location}
                                    errorText={
                                        errors?.other_provider_location?.message
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_provider_location
                                    }
                                />
                            </div>
                        ) : null}
                    </>
                )}
                {watch("appointment_location") ===
                AppointmentLocation.TELETHERAPY ? (
                    <div className="note-checkboxes">
                        <FormCheckbox
                            {...register(
                                "consent_was_received_for_the_teletherapy_session"
                            )}
                            label={
                                TERMINATION_OF_TREATMENT_NOTE_LABELS.consent_was_received_for_the_teletherapy_session
                            }
                            defaultChecked={
                                noteDetails.note_content
                                    ?.consent_was_received_for_the_teletherapy_session
                            }
                        />
                        <FormCheckbox
                            {...register(
                                "the_teletherapy_session_was_conducted_via_video"
                            )}
                            label={
                                TERMINATION_OF_TREATMENT_NOTE_LABELS.the_teletherapy_session_was_conducted_via_video
                            }
                            defaultChecked={
                                noteDetails.note_content
                                    ?.the_teletherapy_session_was_conducted_via_video
                            }
                        />
                    </div>
                ) : null}
                <div className="fg fg-space-between two flex">
                    <Controller
                        name="first_session_date"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    TERMINATION_OF_TREATMENT_NOTE_LABELS.first_session_date
                                }
                                onChange={(date) => {
                                    field.onChange(date);
                                    setLocalFirstSessionDate(date);
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localFirstSessionDate}
                                hasError={!!errors.first_session_date}
                                errorText={
                                    errors.first_session_date?.type ===
                                    "typeError"
                                        ? "Invalid date value"
                                        : errors.first_session_date?.message
                                }
                            />
                        )}
                    />
                    <Controller
                        name="last_session_date"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    TERMINATION_OF_TREATMENT_NOTE_LABELS.last_session_date
                                }
                                onChange={(date) => {
                                    field.onChange(date);
                                    setLocalLastSessionDate(date);
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localLastSessionDate}
                                hasError={!!errors.last_session_date}
                                errorText={
                                    errors.last_session_date?.type ===
                                    "typeError"
                                        ? "Invalid date value"
                                        : errors.last_session_date?.message
                                }
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("total_number_of_sessions")}
                        label={
                            TERMINATION_OF_TREATMENT_NOTE_LABELS.total_number_of_sessions
                        }
                        isRequired
                        hasError={!!errors.total_number_of_sessions}
                        errorText={errors.total_number_of_sessions?.message}
                        defaultValue={noteDetails.note_content.no_of_sessions}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERMINATION_OF_TREATMENT_NOTE_LABELS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                hideSelectedOptions
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERMINATION_OF_TREATMENT_NOTE_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERMINATION_OF_TREATMENT_NOTE_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERMINATION_OF_TREATMENT_NOTE_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERMINATION_OF_TREATMENT_NOTE_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("presenting_problems")}
                        isRequired
                        label={
                            TERMINATION_OF_TREATMENT_NOTE_LABELS.presenting_problems
                        }
                        defaultValue={
                            noteDetails.note_content?.presenting_problems
                        }
                        hasError={!!errors.presenting_problems}
                        errorText={errors.presenting_problems?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("planned_treatment_and_goals")}
                        label={
                            TERMINATION_OF_TREATMENT_NOTE_LABELS.planned_treatment_and_goals
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.planned_treatment_and_goals
                        }
                        isRequired
                        hasError={!!errors.planned_treatment_and_goals}
                        errorText={errors.planned_treatment_and_goals?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("course_of_treatment")}
                        label={
                            TERMINATION_OF_TREATMENT_NOTE_LABELS.course_of_treatment
                        }
                        defaultValue={
                            noteDetails.note_content?.course_of_treatment
                        }
                        isRequired
                        hasError={!!errors.course_of_treatment}
                        errorText={errors.course_of_treatment?.message}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("patient_final_condition")}
                        label={
                            TERMINATION_OF_TREATMENT_NOTE_LABELS.patient_final_condition
                        }
                        defaultValue={
                            noteDetails.note_content?.patient_final_condition
                        }
                        isRequired
                        hasError={!!errors.patient_final_condition}
                        errorText={errors.patient_final_condition?.message}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("prognosis")}
                        label={TERMINATION_OF_TREATMENT_NOTE_LABELS.prognosis}
                        defaultValue={noteDetails.note_content?.prognosis}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("reason_for_termination")}
                        label={
                            TERMINATION_OF_TREATMENT_NOTE_LABELS.reason_for_termination
                        }
                        defaultValue={
                            noteDetails.note_content?.reason_for_termination
                        }
                        isRequired
                        hasError={!!errors.reason_for_termination}
                        errorText={errors.reason_for_termination?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("discharge_plan_and_follow_up")}
                        label={
                            TERMINATION_OF_TREATMENT_NOTE_LABELS.discharge_plan_and_follow_up
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.discharge_plan_and_follow_up
                        }
                        isRequired
                        hasError={!!errors.discharge_plan_and_follow_up}
                        errorText={errors.discharge_plan_and_follow_up?.message}
                    />
                </div>
                <p className="note-label-req">
                    {
                        TERMINATION_OF_TREATMENT_NOTE_LABELS.treatment_modality_intervention
                    }
                </p>
                <div className="note-checkboxes-list">
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Cognitive/Behavioral"
                        value="Cognitive/Behavioral"
                        defaultChecked={isCheckboxChecked(
                            "Cognitive/Behavioral",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Behavioral Modification"
                        value="Behavioral Modification"
                        defaultChecked={isCheckboxChecked(
                            "Behavioral Modification",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Supportive"
                        value="Supportive"
                        defaultChecked={isCheckboxChecked(
                            "Supportive",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Marital/Couples Therapy"
                        value="Marital/Couples Therapy"
                        defaultChecked={isCheckboxChecked(
                            "Marital/Couples Therapy",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Family Therapy"
                        value="Family Therapy"
                        defaultChecked={isCheckboxChecked(
                            "Family Therapy",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Stress Management"
                        value="Stress Management"
                        defaultChecked={isCheckboxChecked(
                            "Stress Management",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Psychodynamic"
                        value="Psychodynamic"
                        defaultChecked={isCheckboxChecked(
                            "Psychodynamic",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Parent Training"
                        value="Parent Training"
                        defaultChecked={isCheckboxChecked(
                            "Parent Training",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Crisis Intervention"
                        value="Crisis Intervention"
                        defaultChecked={isCheckboxChecked(
                            "Crisis Intervention",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                    <FormCheckbox
                        {...register("treatment_modality_intervention")}
                        label="Other"
                        value="Other"
                        defaultChecked={isCheckboxChecked(
                            "Other",
                            noteDetails.note_content
                                ?.treatment_modality_intervention
                        )}
                    />
                </div>
                {errors.treatment_modality_intervention ? (
                    <div className="note-error">
                        <p>This field is required</p>
                    </div>
                ) : null}
                <div className="fg">
                    <Input
                        {...register("other_treatment_modality_intervention")}
                        label={
                            TERMINATION_OF_TREATMENT_NOTE_LABELS.other_treatment_modality_intervention
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.other_treatment_modality_intervention
                        }
                        isOptional
                    />
                </div>
                <h3 className="note-label-opt">
                    {TERMINATION_OF_TREATMENT_NOTE_LABELS.files}
                </h3>
                <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                    <FilePicker.FilePicker
                        onDrop={onDrop}
                        disabled={isAddDocMutating > 0}
                    >
                        <FilePicker.FilePickerIcon />
                        <FilePicker.FilePickerMeta description="Click to upload note files" />
                    </FilePicker.FilePicker>
                    {selectedFiles && selectedFiles.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <FilePicker.FilePickerSelectedFiles
                                selectedFiles={selectedFiles}
                                handleRemoveSelectedFile={
                                    handleRemoveSelectedFile
                                }
                                wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                            />
                            <Button
                                className="self-start"
                                onClick={() =>
                                    handleAddNoteDocument(selectedFiles, () =>
                                        setSelectedFiles(null)
                                    )
                                }
                                disabled={isAddDocMutating > 0}
                            >
                                Save files
                            </Button>
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <Progress.Root value={uploadProgress}>
                                    <Progress.Label>
                                        Uploading {selectedFiles.length}{" "}
                                        {selectedFiles.length === 1
                                            ? "file"
                                            : "files"}
                                        ...
                                    </Progress.Label>
                                    <Progress.Value />
                                    <Progress.Track>
                                        <Progress.Indicator />
                                    </Progress.Track>
                                </Progress.Root>
                            )}
                        </div>
                    ) : null}
                    {noteDetails?.note_content_documents &&
                    noteDetails?.note_content_documents.length > 0 ? (
                        <div className="flex flex-col gap-y-[var(--spacing-items)]">
                            {noteDetails.note_content_documents.map((doc) => (
                                <div
                                    key={doc.note_content_document_id}
                                    className="flex items-center gap-x-3"
                                >
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="h-7 !text-text text-caption underline"
                                        actionTitle="View file"
                                        onClick={() => {
                                            setDocInView(doc);
                                        }}
                                    >
                                        <span>{`${displayNameInRightFormat(
                                            {
                                                firstName:
                                                    doc.uploaded_by.first_name,
                                                lastName:
                                                    doc.uploaded_by.last_name,
                                            },
                                            false
                                        )} -  ${doc.document_name}`}</span>
                                    </ActionButton>
                                    <ActionButton
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        size="auto"
                                        className="h-7"
                                        onClick={() =>
                                            handleDeleteNoteContentDocument(
                                                doc.note_content_document_id
                                            )
                                        }
                                        disabled={isDeleteDocMutating > 0}
                                        actionTitle="Delete file from note content"
                                    >
                                        <DeleteIcon />
                                    </ActionButton>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                <Alert
                    type="warning"
                    description="Please note, signing this note will terminate the client's session. Please ensure you terminate the session on intake as well."
                />

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </div>
    );
}
